import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

// Cache to avoid redundant API calls
const masterCache: Record<string, { data: MasterItem[]; ts: number }> = {};
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

export interface MasterItem {
  code: string;
  value: string;
  extraData?: any;
  sortOrder?: number;
}

/**
 * Hook to load master data for any dropdown type.
 * Usage: const { options, loading } = useMaster('GENDER');
 *
 * Returns array of { code, value } objects.
 * Use code as the form value, value as the display label.
 */
export function useMaster(masterType: string) {
  const [options, setOptions] = useState<MasterItem[]>([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!masterType) return;

    // Check cache
    const cached = masterCache[masterType];
    if (cached && Date.now() - cached.ts < CACHE_TTL) {
      setOptions(cached.data);
      return;
    }

    setLoading(true);
    try {
      const r = await api.getMasterByType(masterType);
      const data = r.data || [];
      masterCache[masterType] = { data, ts: Date.now() };
      setOptions(data);
    } catch {
      // Fallback to empty array — pages should have hardcoded fallbacks
    } finally {
      setLoading(false);
    }
  }, [masterType]);

  useEffect(() => { load(); }, [load]);

  return { options, loading, reload: load };
}

/**
 * Hook to load multiple master types at once.
 * Usage: const masters = useMultiMaster(['GENDER', 'BLOOD_GROUP', 'DEPARTMENT']);
 */
export function useMultiMaster(types: string[]) {
  const [masters, setMasters] = useState<Record<string, MasterItem[]>>({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!types.length) return;
    setLoading(true);
    Promise.all(
      types.map(async t => {
        const cached = masterCache[t];
        if (cached && Date.now() - cached.ts < CACHE_TTL) return { type: t, data: cached.data };
        try {
          const r = await api.getMasterByType(t);
          const data = r.data || [];
          masterCache[t] = { data, ts: Date.now() };
          return { type: t, data };
        } catch {
          return { type: t, data: [] };
        }
      })
    ).then(results => {
      const map: Record<string, MasterItem[]> = {};
      for (const { type, data } of results) map[type] = data;
      setMasters(map);
    }).finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [types.join(',')]);

  return { masters, loading };
}

/** Get master options synchronously from cache (use after useMaster/useMultiMaster has loaded) */
export function getMasterOptions(masterType: string): MasterItem[] {
  return masterCache[masterType]?.data || [];
}

/** Invalidate cache for a type (call after updating masters) */
export function invalidateMasterCache(masterType?: string) {
  if (masterType) delete masterCache[masterType];
  else Object.keys(masterCache).forEach(k => delete masterCache[k]);
}
